/**
 * Route Rating Modal Component
 * Allows users to rate a route (1-5 stars) and add a comment/photos.
 */

import { useState, useEffect } from "react";
import { Star, Trash2, Loader2 } from "lucide-react";
import { ImageCaptureInput } from "@/components/ImageCaptureInput";
import { Button } from "@/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/lib/authContext";
import { useI18n } from "@/lib/i18n";

interface RouteRatingModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    routeId: string;
    routeName: string;
    existingReview?: {
        rating: number;
        comment?: string | null;
        photoUrls?: string[];
    } | null;
    onSuccess?: () => void;
}

export function RouteRatingModal({
    open,
    onOpenChange,
    routeId,
    routeName,
    existingReview,
    onSuccess,
}: RouteRatingModalProps) {
    const { t } = useI18n();
    const { user } = useAuth();
    const [rating, setRating] = useState(existingReview?.rating ?? 0);
    const [comment, setComment] = useState(existingReview?.comment ?? "");
    const [images, setImages] = useState<string[]>([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (open && existingReview) {
            setRating(existingReview.rating);
            setComment(existingReview.comment ?? "");
            setImages(existingReview.photoUrls ?? []);
        } else if (open) {
            setRating(0);
            setComment("");
            setImages([]);
        }
    }, [open, existingReview]);

    async function handleSubmit() {
        if (!user) {
            setError(t("rating.loginRequired"));
            return;
        }

        if (rating === 0) {
            setError(t("rating.allRequired"));
            return;
        }

        setIsSubmitting(true);
        setError(null);

        try {
            const { apiRequest } = await import("@/lib/queryClient");
            await apiRequest("POST", `/api/routes/${routeId}/reviews`, {
                rating,
                comment: comment.trim() || null,
                photoUrls: images.slice(0, 4),
            });

            onSuccess?.();
            onOpenChange(false);
        } catch (err: any) {
            console.error("Error submitting review:", err);
            setError(err.message);
        } finally {
            setIsSubmitting(false);
        }
    }

    async function handleDelete() {
        if (!confirm(t("rating.confirmDelete"))) return;

        setIsSubmitting(true);
        try {
            const { apiRequest } = await import("@/lib/queryClient");
            await apiRequest("DELETE", `/api/routes/${routeId}/reviews`);

            onSuccess?.();
            onOpenChange(false);
        } catch (err: any) {
            setError(err.message);
        } finally {
            setIsSubmitting(false);
        }
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-lg">
                <DialogHeader>
                    <DialogTitle>{t("rating.title")} {routeName}</DialogTitle>
                    <DialogDescription>{t("rating.desc")}</DialogDescription>
                </DialogHeader>

                <div className="space-y-6">
                    <div className="space-y-2">
                        <Label>{t("rating.overall")}</Label>
                        <div className="flex gap-2">
                            {[1, 2, 3, 4, 5].map((star) => (
                                <button
                                    key={star}
                                    onClick={() => setRating(star)}
                                    className="focus:outline-none transition-transform hover:scale-110"
                                >
                                    <Star
                                        className={`h-8 w-8 ${star <= rating ? "text-yellow-500 fill-yellow-500" : "text-muted-foreground fill-muted-foreground/20"}`}
                                    />
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="space-y-2">
                        <Label>{t("rating.commentOptional")}</Label>
                        <Textarea
                            value={comment}
                            onChange={(e) => setComment(e.target.value)}
                            placeholder={t("rating.commentPlaceholder")}
                            rows={4}
                        />
                    </div>

                    <div className="space-y-2">
                        <Label>{t("rating.photosOptional")}</Label>
                        <div className="border-2 border-dashed border-border rounded-lg p-4 text-center">
                            <ImageCaptureInput
                                multiple
                                onImages={(newImages) => setImages([...images, ...newImages].slice(0, 4))}
                                label={t("rating.addPhotos")}
                            />
                        </div>
                        {images.length > 0 && (
                            <div className="grid grid-cols-4 gap-2 mt-2">
                                {images.map((img, idx) => (
                                    <div key={idx} className="relative aspect-square">
                                        <img src={img} className="w-full h-full object-cover rounded-md" />
                                        <button
                                            onClick={() => setImages(images.filter((_, i) => i !== idx))}
                                            className="absolute top-1 right-1 p-1 bg-black/50 text-white rounded-full"
                                        >
                                            <Trash2 className="h-3 w-3" />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {error && <div className="text-destructive text-sm">{error}</div>}

                    <div className="flex justify-end gap-2">
                        {existingReview && (
                            <Button variant="ghost" className="text-destructive mr-auto" onClick={handleDelete}>{t("common.delete")}</Button>
                        )}
                        <Button variant="outline" onClick={() => onOpenChange(false)}>{t("shops.cancel")}</Button>
                        <Button onClick={handleSubmit} disabled={isSubmitting || rating === 0}>
                            {isSubmitting ? <Loader2 className="animate-spin h-4 w-4" /> : t("rating.save")}
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
